import { useEffect, useMemo, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { agendaLabService } from '../../services/api'
import {
  DAY_NAMES,
  asArray,
  buildDayBlocks,
  formatMinutesLabel,
  formatTimeLabel,
  normalizeAppointment,
  normalizeRule
} from './scheduleUtils'

const unwrapApiData = (response) => response?.data?.data ?? response?.data ?? {}

const dateTitle = (date) =>
  new Intl.DateTimeFormat('es-CO', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric'
  }).format(date)

export default function AgendaLabScheduleDay() {
  const { date } = useParams()
  const [timezone, setTimezone] = useState('America/Bogota')
  const [rules, setRules] = useState([])
  const [appointments, setAppointments] = useState([])
  const [error, setError] = useState('')

  const selectedDate = useMemo(() => {
    const parsed = new Date(`${date}T00:00:00`)
    return Number.isNaN(parsed.getTime()) ? new Date() : parsed
  }, [date])

  useEffect(() => {
    const load = async () => {
      try {
        setError('')
        const start = new Date(selectedDate)
        start.setHours(0, 0, 0, 0)
        const end = new Date(selectedDate)
        end.setDate(end.getDate() + 1)
        end.setHours(0, 0, 0, 0)

        const [scheduleResponse, calendarResponse] = await Promise.all([
          agendaLabService.getSchedule(),
          agendaLabService.getCalendar(start.toISOString(), end.toISOString())
        ])

        const schedule = unwrapApiData(scheduleResponse)
        const calendarItems = unwrapApiData(calendarResponse)
        setTimezone(schedule?.timezone || schedule?.Timezone || 'America/Bogota')
        setRules(asArray(schedule?.rules || schedule?.Rules).map(normalizeRule))
        setAppointments(asArray(calendarItems).map(normalizeAppointment))
      } catch (err) {
        setError(err?.message || 'No se pudo cargar la programacion del dia.')
      }
    }

    load()
  }, [selectedDate])

  const blocks = useMemo(() => buildDayBlocks({ date: selectedDate, rules, appointments }), [appointments, rules, selectedDate])
  const scheduledCount = blocks.filter((block) => block.type === 'scheduled').length
  const availableCount = blocks.filter((block) => block.type === 'available').length
  const weekdayName = DAY_NAMES[selectedDate.getDay()]

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 capitalize">{dateTitle(selectedDate)}</h1>
          <p className="text-sm text-gray-500 mt-1">
            Zona horaria: {timezone}. Verde: franjas ya agendadas. Rojo tenue: franjas disponibles.
          </p>
        </div>
        <Link to="/agendalab/schedule" className="btn btn-secondary">
          Volver a programacion
        </Link>
      </div>

      {error && <div className="card p-3 text-sm text-red-700 bg-red-50 border border-red-200">{error}</div>}

      <div className="grid gap-3 md:grid-cols-3">
        <div className="rounded-2xl border border-gray-200 bg-white p-4">
          <p className="text-sm text-gray-500">Dia seleccionado</p>
          <p className="mt-2 text-xl font-semibold text-gray-900">{weekdayName}</p>
        </div>
        <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-4">
          <p className="text-sm text-emerald-700">Citas programadas</p>
          <p className="mt-2 text-2xl font-bold text-emerald-900">{scheduledCount}</p>
        </div>
        <div className="rounded-2xl border border-rose-200 bg-rose-50 p-4">
          <p className="text-sm text-rose-700">Franjas disponibles</p>
          <p className="mt-2 text-2xl font-bold text-rose-900">{availableCount}</p>
        </div>
      </div>

      <section className="card p-5 space-y-4">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Programacion del dia</h2>
          <p className="text-sm text-gray-500">Cada bloque refleja tu horario real para ese dia.</p>
        </div>

        <div className="space-y-3">
          {blocks.map((block) => (
            <div
              key={block.id}
              className={`rounded-2xl border p-4 ${block.type === 'scheduled' ? 'border-emerald-200 bg-emerald-50' : 'border-rose-200 bg-rose-50'}`}
            >
              <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
                <div>
                  <p className={`text-sm font-semibold ${block.type === 'scheduled' ? 'text-emerald-900' : 'text-rose-900'}`}>
                    {block.type === 'scheduled' ? `${block.appointment.serviceName} con ${block.appointment.clientName}` : 'Franja disponible'}
                  </p>
                  <p className={`text-sm ${block.type === 'scheduled' ? 'text-emerald-700' : 'text-rose-700'}`}>
                    {formatTimeLabel(block.start)} - {formatTimeLabel(block.end)}
                  </p>
                </div>

                <div className={`text-sm font-medium ${block.type === 'scheduled' ? 'text-emerald-700' : 'text-rose-700'}`}>
                  {formatMinutesLabel((block.end - block.start) / 60000)}
                </div>
              </div>

              {block.type === 'scheduled' && (
                <div className="mt-2 space-y-1 text-xs text-emerald-800">
                  <div>{block.appointment.clientEmail || 'Sin email registrado'}</div>
                  <div>{block.appointment.notes ? `Notas: ${block.appointment.notes}` : 'Sin notas registradas.'}</div>
                </div>
              )}
            </div>
          ))}

          {blocks.length === 0 && (
            <div className="rounded-2xl border border-gray-200 bg-gray-50 p-6 text-sm text-gray-600">
              No tienes bloques configurados para este dia. Revisa tu horario semanal en la pantalla de programacion.
            </div>
          )}
        </div>
      </section>
    </div>
  )
}
